import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authFetch } from '../services/authFetch';

const WaliKelasReportsDetailPage = () => {
  const navigate = useNavigate();
  const { reportType } = useParams();
  const [searchParams] = useSearchParams();
  const className = searchParams.get('class');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [apresiasiOptions, setApresiasiOptions] = useState([]);

  // Report type configurations
  const reportConfigs = {
    kts: {
      title: 'Laporan KTS',
      icon: '📋',
      endpoint: 'kts-reports',
      adminEndpoint: 'kts-reports',
      statusField: 'status'
    },
    interupsi: {
      title: 'Ijin Interupsi',
      icon: '⏸️',
      endpoint: 'interuption-permits',
      adminEndpoint: 'interuption-permits',
      statusField: 'request_status'
    },
    'tidak-masuk': {
      title: 'Ijin Tidak Masuk',
      icon: '🚫',
      endpoint: 'absent-permits',
      statusField: 'request_status'
    },
    lembur: {
      title: 'Laporan Lembur',
      icon: '🕐',
      endpoint: 'overtime-reports',
      statusField: 'request_status'
    },
    kersos: {
      title: 'Laporan Kersos',
      icon: '🏞️',
      endpoint: 'kersos-reports',
      statusField: 'request_status'
    },
    psm: {
      title: 'Laporan PSM',
      icon: '🔧',
      endpoint: 'psm-reports',
      statusField: 'request_status'
    },
    konseling: {
      title: 'Laporan Konseling',
      icon: '💬',
      endpoint: 'konseling',
      statusField: 'status'
    },
    sp: {
      title: 'SP',
      icon: '⚠️',
      endpoint: 'warning-letters',
      adminEndpoint: 'surat-peringatan',
      statusField: 'status'
    }
  };

  const currentConfig = reportConfigs[reportType];

  // Fetch apresiasi options for PSM reports (admin only)
  useEffect(() => {
    if (reportType === 'psm') {
      const userRole = localStorage.getItem('userRole');
      
      // Only fetch apresiasi options for admin users
      // Teachers will get apresiasi data directly from PSM reports
      if (userRole === 'admin_user') {
        async function fetchApresiasiOptions() {
          try {
            const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
            const response = await authFetch(`${apiBase}/api/apresiasis?per_page=1000`, {
              headers: { 'Accept': 'application/json' }
            });
            
            let data = [];
            try {
              const result = await response.json();
              data = result.data || result || [];
            } catch (err) {
              console.error('Error parsing apresiasi response:', err);
              data = [];
            }
            setApresiasiOptions(data);
          } catch (err) {
            console.error('Failed to fetch apresiasi options:', err);
            setApresiasiOptions([]);
          }
        }
        fetchApresiasiOptions();
      }
    }
  }, [reportType]);

  useEffect(() => {
    if (!currentConfig || !className) {
      setError('Invalid report type or missing class parameter');
      setLoading(false);
      return;
    }

    fetchReports();
  }, [reportType, className, apresiasiOptions]);

  const fetchReports = async () => {
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const userRole = localStorage.getItem('userRole');
      
      let url;
      if (userRole === 'admin_user') {
        // Use admin endpoints
        const endpoint = currentConfig.adminEndpoint || currentConfig.endpoint;
        url = `${apiBase}/api/admin/${endpoint}`;
      } else {
        // Use teacher endpoints with class filter
        url = `${apiBase}/api/teacher/${currentConfig.endpoint}?class=${encodeURIComponent(className)}`;
      }
      
      const res = await authFetch(url, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!res.ok) {
        throw new Error(`Failed to fetch ${currentConfig.title}: ${res.status}`);
      }
      
      const data = await res.json();
      let items = data.data || data || [];
      
      // For admin users, filter by class
      if (userRole === 'admin_user' && className) {
        const filteredItems = [];
        items.forEach(item => {
          // Handle different data structures based on report type
          if (reportType === 'kts' || reportType === 'konseling') {
            // KTS and konseling reports have a students array
            if (item.students && Array.isArray(item.students)) {
              item.students.forEach(student => {
                const studentClass = student.class || student.kelas || student.class_name || student.student_class;
                if (studentClass === className) {
                  // Create a separate row for each student
                  filteredItems.push({
                    ...item,
                    nis: student.nis || student.student_id,
                    student_name: student.name || student.student_name,
                    student: {
                      id: student.student_id || student.id,
                      nis: student.nis || student.student_id,
                      name: student.name || student.student_name,
                      class: studentClass
                    }
                  });
                }
              });
            }
          } else {
            // Other reports have a single student object
            const student = item.student;
            if (student) {
              const studentClass = student.class || student.kelas || student.class_name;
              if (studentClass === className) {
                // Process appreciation data for PSM reports
                let apresiasiDescription = '';
                let apresiasiPoint = 0;
                
                if (reportType === 'psm') {
                  // For admin users, use apresiasiOptions array
                  if (userRole === 'admin_user' && item.apresiasis_id && apresiasiOptions.length > 0) {
                    const apresiasi = apresiasiOptions.find(opt => opt.id === parseInt(item.apresiasis_id));
                    if (apresiasi) {
                      apresiasiDescription = apresiasi.description || apresiasi.name || 'N/A';
                      apresiasiPoint = apresiasi.point || 0;
                    } else {
                      apresiasiDescription = `ID: ${item.apresiasis_id}`;
                      apresiasiPoint = 0;
                    }
                  }
                  // For teachers, use embedded apresiasi relationship data
                  else if (item.apresiasi) {
                    apresiasiDescription = item.apresiasi.description || item.apresiasi.name || 'N/A';
                    apresiasiPoint = item.apresiasi.point || 0;
                  }
                  // Fallback for other cases
                  else if (item.apresiasis_id) {
                    apresiasiDescription = `ID: ${item.apresiasis_id}`;
                    apresiasiPoint = 0;
                  } else {
                    apresiasiDescription = 'N/A';
                    apresiasiPoint = 0;
                  }
                }
                
                // Add the item with proper field mapping
                const mappedItem = {
                  ...item,
                  nis: student.nis,
                  student_name: student.name || student.student_name,
                  // Ensure student object is properly formatted
                  student: {
                    id: student.id,
                    nis: student.nis,
                    name: student.name,
                    class: studentClass
                  }
                };
                
                // Add processed appreciation data for PSM reports
                if (reportType === 'psm') {
                  mappedItem.apresiasi = apresiasiDescription;
                  mappedItem.apresiasiPoint = apresiasiPoint;
                }
                
                filteredItems.push(mappedItem);
              }
            }
          }
        });
        items = filteredItems;
      } else {
        // For non-admin users, process appreciation data for PSM reports
        if (reportType === 'psm') {
          items = items.map(item => {
            let apresiasiDescription = '';
            let apresiasiPoint = 0;
            
            // Use embedded apresiasi relationship data for teachers
            if (item.apresiasi) {
              apresiasiDescription = item.apresiasi.description || item.apresiasi.name || 'N/A';
              apresiasiPoint = item.apresiasi.point || 0;
            } else if (item.apresiasis_id) {
              apresiasiDescription = `ID: ${item.apresiasis_id}`;
              apresiasiPoint = 0;
            } else {
              apresiasiDescription = 'N/A';
              apresiasiPoint = 0;
            }
            
            return {
              ...item,
              apresiasi: apresiasiDescription,
              apresiasiPoint: apresiasiPoint
            };
          });
        }
      }
      
      setReports(Array.isArray(items) ? items : []);
      setLoading(false);
    } catch (e) {
      console.error('Error fetching reports:', e);
      setError(e.message);
      setLoading(false);
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('id-ID');
    } catch {
      return dateString;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'Menunggu',
      'menunggu': 'Menunggu',
      'approved': 'Disetujui',
      'disetujui': 'Disetujui',
      'rejected': 'Ditolak',
      'ditolak': 'Ditolak',
      'completed': 'Selesai',
      'selesai': 'Selesai'
    };
    return statusMap[status?.toLowerCase()] || status || '-';
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'pending': '#f59e0b',
      'menunggu': '#f59e0b',
      'approved': '#10b981',
      'disetujui': '#10b981',
      'rejected': '#ef4444',
      'ditolak': '#ef4444',
      'completed': '#6366f1',
      'selesai': '#6366f1'
    };
    return statusColors[status?.toLowerCase()] || '#6b7280';
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingText>Memuat data {currentConfig?.title?.toLowerCase()}...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
          <RefreshButton onClick={() => window.location.reload()}>
            Muat Ulang
          </RefreshButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (!currentConfig) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorText>Tipe laporan tidak valid</ErrorText>
          <RefreshButton onClick={() => navigate(-1)}>
            Kembali
          </RefreshButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <HeaderContent>
          <div>
            <BackButton onClick={() => navigate(-1)}>
              ← Kembali
            </BackButton>
            <h1>{currentConfig.icon} {currentConfig.title}</h1>
            <Subtitle>Kelas {className} • Total: {reports.length} item</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      {reports.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>{currentConfig.icon}</EmptyStateIcon>
          <EmptyStateText>Tidak ada {currentConfig.title.toLowerCase()} untuk kelas {className}</EmptyStateText>
        </EmptyState>
      ) : (
        <ReportsTable>
          <thead>
            <tr>
              <th>Tanggal</th>
              <th>NIS</th>
              <th>Nama Siswa</th>
              {reportType === 'interupsi' ? (
                <>
                  <th>Sedang apa</th>
                  <th>Pergi jam</th>
                  <th>Kembali</th>
                  <th>Keperluan</th>
                </>
              ) : reportType === 'tidak-masuk' ? (
                <>
                  <th>Dari</th>
                  <th>Sampai</th>
                  <th>Keperluan</th>
                </>
              ) : reportType === 'lembur' ? (
                <>
                  <th>Tanggal Lembur</th>
                  <th>Jam Mulai</th>
                  <th>Jam Selesai</th>
                </>
              ) : reportType === 'kersos' ? (
                <>
                  <th>Jam Mulai</th>
                  <th>Jam Selesai</th>
                  <th>Jenis Pekerjaan</th>
                </>
              ) : reportType === 'psm' ? (
                <>
                  <th>Jam Mulai</th>
                  <th>Jam Selesai</th>
                  <th>Apresiasi</th>
                </>
              ) : reportType === 'konseling' ? (
                <>
                  <th>Tipe</th>
                </>
              ) : reportType === 'sp' ? (
                <>
                  <th>Tipe Peringatan</th>
                  <th>Total Poin</th>
                </>
              ) : (
                <>
                  <th>Pelapor</th>
                  <th>Keterangan</th>
                </>
              )}
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report, index) => (
              <tr key={report.id || index}>
                <td>{formatDate(report.created_at || report.date || report.tanggal)}</td>
                <td>{report.nis || report.student?.nis || '-'}</td>
                <td>{report.student_name || report.student?.name || report.nama_siswa || '-'}</td>
                {reportType === 'interupsi' ? (
                   <>
                     <td>{report.sedangApa || report.current_activity || '-'}</td>
                     <td>{report.pergiJam || report.leave_hour || '-'}</td>
                     <td>{report.kembali || report.return_hour || '-'}</td>
                     <td>
                       <KeteranganCell>
                         {report.keperluan || report.purpose || '-'}
                       </KeteranganCell>
                     </td>
                   </>
                ) : reportType === 'tidak-masuk' ? (
                  <>
                    <td>{formatDate(report.tanggalMulai || report.from_date || report.start_date)}</td>
                    <td>{formatDate(report.tanggalSelesai || report.to_date || report.end_date)}</td>
                    <td>
                      <KeteranganCell>
                        {report.keperluan || report.purpose || '-'}
                      </KeteranganCell>
                    </td>
                  </>
                ) : reportType === 'lembur' ? (
                  <>
                    <td>{formatDate(report.tanggalLembur || report.overtime_date)}</td>
                    <td>{report.jamMulai || report.start_time || '-'}</td>
                    <td>{report.jamSelesai || report.finish_time || report.end_time || '-'}</td>
                  </>
                ) : reportType === 'kersos' ? (
                   <>
                     <td>{report.jamMulai || report.start_time || '-'}</td>
                     <td>{report.jamSelesai || report.finish_time || report.end_time || '-'}</td>
                     <td>{report.jenisPekerjaan || report.type_or_work || report.work_type || report.job_type || '-'}</td>
                   </>
                ) : reportType === 'psm' ? (
                   <>
                     <td>{report.jamMulai || report.start_time || '-'}</td>
                     <td>{report.jamSelesai || report.finish_time || report.end_time || '-'}</td>
                     <td>{report.apresiasi?.description || report.apresiasi?.name || report.apresiasi || '-'}</td>
                   </>
                ) : reportType === 'konseling' ? (
                  <>
                    <td>
                      <KeteranganCell>
                        {report.type || report.tipe_pendampingan || report.counseling_type || '-'}
                      </KeteranganCell>
                    </td>
                  </>
                ) : reportType === 'sp' ? (
                  <>
                    <td>
                      <TypeBadge type={report.tipe || report.warning_type || report.type}>
                        {report.tipe || report.warning_type || report.type || '-'}
                      </TypeBadge>
                    </td>
                    <td>{report.points || report.total_poin || '-'}</td>
                  </>
                ) : (
                  <>
                    <td>{report.teacher_name || report.teacherName || report.teacher?.name || report.admin_user?.name || '-'}</td>
                    <td>
                      <KeteranganCell>
                        {report.keterangan || report.description || report.reason || report.catatan || '-'}
                      </KeteranganCell>
                    </td>
                  </>
                )}
                <td>
                  <StatusBadge status={report[currentConfig.statusField] || report.request_status || report.status_pengajuan}>
                    {getStatusText(report[currentConfig.statusField] || report.request_status || report.status_pengajuan)}
                  </StatusBadge>
                </td>
                <td>
                  <ActionButton onClick={() => handleViewReport(report)}>
                    Lihat Detail
                  </ActionButton>
                </td>
              </tr>
            ))}
          </tbody>
        </ReportsTable>
      )}

      {/* Modal for viewing report details */}
      {showModal && selectedReport && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{currentConfig.icon} Detail {currentConfig.title}</ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <DetailForm>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Tanggal</FormLabel>
                    <FormInput 
                      type="text" 
                      value={formatDate(selectedReport.created_at || selectedReport.date || selectedReport.tanggal)} 
                      readOnly 
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Status</FormLabel>
                    <StatusBadge status={selectedReport[currentConfig.statusField] || selectedReport.request_status || selectedReport.status_pengajuan}>
                      {getStatusText(selectedReport[currentConfig.statusField] || selectedReport.request_status || selectedReport.status_pengajuan)}
                    </StatusBadge>
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <FormLabel>NIS</FormLabel>
                    <FormInput 
                      type="text" 
                      value={selectedReport.nis || selectedReport.student?.nis || '-'} 
                      readOnly 
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Nama Siswa</FormLabel>
                    <FormInput 
                      type="text" 
                      value={selectedReport.student_name || selectedReport.student?.name || selectedReport.nama_siswa || '-'} 
                      readOnly 
                    />
                  </FormGroup>
                </FormRow>

                {/* Additional fields based on report type */}
                {reportType === 'kts' && (
                  <>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Tipe</FormLabel>
                        <FormInput type="text" value={(selectedReport.tipe || selectedReport.class_type) === 'teori' ? 'Teori' : 'Praktek'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Kategori</FormLabel>
                        <FormInput type="text" value={selectedReport.category || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Jenis Ketidakhadiran</FormLabel>
                        <FormInput type="text" value={selectedReport.absent_type || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Section</FormLabel>
                        <FormInput type="text" value={selectedReport.section || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Shift</FormLabel>
                        <FormInput type="text" value={selectedReport.shift || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Ketidaksesuaian</FormLabel>
                        <FormInput type="text" value={selectedReport.ketidaksesuaian || selectedReport.description || 'Alfa tidak masuk'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>
                          {(selectedReport.tipe || selectedReport.class_type) === 'teori' ? 'Jam Lembur' : 'Jam Kompensasi'}
                        </FormLabel>
                        <FormInput type="text" value={selectedReport.total_hour || '0'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Jam Kersos</FormLabel>
                        <FormInput type="text" value={selectedReport.total_hour_kersos || '0'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Poin Sikap</FormLabel>
                        <FormInput type="text" value={selectedReport.appreciation_point || '0'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Pelapor</FormLabel>
                        <FormInput type="text" value={selectedReport.teacher_name || selectedReport.teacherName || selectedReport.teacher?.name || selectedReport.admin_user?.name || 'Tidak diketahui'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup fullWidth>
                        <FormLabel>Konsekuensi Tambahan</FormLabel>
                        <FormTextarea 
                          value={selectedReport.additional_consequence || '-'} 
                          readOnly 
                        />
                      </FormGroup>
                    </FormRow>

                  </>
                )}

                {reportType === 'interupsi' && (
                  <>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Tanggal Pengajuan</FormLabel>
                        <FormInput type="text" value={
                          selectedReport.tanggalPengajuan ? 
                            new Date(selectedReport.tanggalPengajuan).toLocaleDateString('id-ID', {
                              day: 'numeric',
                              month: 'numeric',
                              year: 'numeric'
                            }) : 
                            selectedReport.created_at ? 
                              new Date(selectedReport.created_at).toLocaleDateString('id-ID', {
                                day: 'numeric',
                                month: 'numeric', 
                                year: 'numeric'
                              }) : '-'
                        } readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Kelas</FormLabel>
                        <FormInput type="text" value={selectedReport.kelas || selectedReport.class || selectedReport.student?.class || selectedReport.student?.kelas || selectedReport.student?.class_name || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Sedang apa?</FormLabel>
                        <FormInput type="text" value={selectedReport.sedangApa || selectedReport.current_activity || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Pergi jam berapa</FormLabel>
                        <FormInput type="text" value={selectedReport.pergiJam || selectedReport.leave_hour || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Kembali</FormLabel>
                        <FormInput type="text" value={selectedReport.kembali || selectedReport.return_hour || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Siswa kembali ke sekolah</FormLabel>
                        <FormInput type="text" value={selectedReport.is_returning ? 'Ya' : 'Tidak'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Ini adalah acara sekolah</FormLabel>
                        <FormInput type="text" value={selectedReport.isSchoolActivity || selectedReport.is_school_activity ? 'Ya' : 'Tidak'} readOnly />
                      </FormGroup>
                      {!selectedReport.is_returning && (
                        <FormGroup>
                          <FormLabel>Jam Lembur</FormLabel>
                          <FormInput type="text" value={selectedReport.jam_lembur || '-'} readOnly />
                        </FormGroup>
                      )}
                    </FormRow>
                    <FormRow>
                      <FormGroup fullWidth>
                        <FormLabel>Ada keperluan apa</FormLabel>
                        <FormTextarea 
                          value={selectedReport.keperluan || selectedReport.purpose || '-'} 
                          readOnly 
                        />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup fullWidth>
                        <FormLabel>Nama tempat dan alamat tujuan</FormLabel>
                        <FormTextarea 
                          value={selectedReport.tempatTujuan || selectedReport.destination || '-'} 
                          readOnly 
                        />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Nama penanggungjawab</FormLabel>
                        <FormInput type="text" value={selectedReport.penanggungjawab || selectedReport.pic || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Hubungan</FormLabel>
                        <FormInput type="text" value={selectedReport.hubungan || selectedReport.relationship || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Nomor telepon penanggungjawab</FormLabel>
                        <FormInput type="text" value={selectedReport.nomorTelepon || selectedReport.pic_contact_number || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                  </>
                )}

                {reportType === 'tidak-masuk' && (
                  <>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Tanggal Pengajuan</FormLabel>
                        <FormInput type="text" value={formatDate(selectedReport.tanggalPengajuan || selectedReport.created_at)} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Kelas</FormLabel>
                        <FormInput type="text" value={selectedReport.kelas || selectedReport.class || selectedReport.student?.class || selectedReport.student?.kelas || selectedReport.student?.class_name || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Tanggal Mulai</FormLabel>
                        <FormInput type="text" value={formatDate(selectedReport.tanggalMulai || selectedReport.from_date || selectedReport.start_date)} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Tanggal Selesai</FormLabel>
                        <FormInput type="text" value={formatDate(selectedReport.tanggalSelesai || selectedReport.to_date || selectedReport.end_date)} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Jenis Ijin</FormLabel>
                        <FormInput type="text" value={selectedReport.type || selectedReport.permit_type || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Aktivitas</FormLabel>
                        <FormInput type="text" value={selectedReport.activity || 'Teori'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup fullWidth>
                        <FormLabel>Keperluan</FormLabel>
                        <FormTextarea 
                          value={selectedReport.keperluan || selectedReport.purpose || '-'} 
                          readOnly 
                        />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup fullWidth>
                        <FormLabel>Nama tempat dan alamat tujuan</FormLabel>
                        <FormTextarea 
                          value={selectedReport.tempatTujuan || selectedReport.destination || '-'} 
                          readOnly 
                        />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Nama penanggungjawab</FormLabel>
                        <FormInput type="text" value={selectedReport.penanggungjawab || selectedReport.pic || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Hubungan</FormLabel>
                        <FormInput type="text" value={selectedReport.hubungan || selectedReport.relationship || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Nomor telepon penanggungjawab</FormLabel>
                        <FormInput type="text" value={selectedReport.nomorTelepon || selectedReport.pic_contact_number || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup fullWidth>
                        <FormLabel>Lampiran Gambar</FormLabel>
                        {selectedReport.images && selectedReport.images.length > 0 ? (
                          <ImagesGrid>
                            {selectedReport.images.map((image, index) => {
                              // Handle different image formats similar to AdminSuratIjinTidakMasukPage
                              let imageSrc = '';
                              if (typeof image === 'string') {
                                imageSrc = image;
                              } else if (image && image.image_url) {
                                // API returns image objects with image_url property
                                const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                                imageSrc = image.image_url.startsWith('http') 
                                  ? image.image_url 
                                  : `${baseUrl}/${image.image_url}`;
                              }
                              
                              if (!imageSrc) {
                                return null;
                              }
                              
                              return (
                                <ImageContainer key={index}>
                                  <img
                                    src={imageSrc}
                                    alt={`Lampiran ${index + 1}`}
                                    onClick={() => window.open(imageSrc, '_blank')}
                                    onError={(e) => {
                                      console.error('Image failed to load:', imageSrc);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </ImageContainer>
                              );
                            })}
                          </ImagesGrid>
                        ) : (
                          <NoImageText>Tidak ada lampiran gambar</NoImageText>
                        )}
                      </FormGroup>
                    </FormRow>
                  </>
                )}

                {reportType === 'lembur' && (
                  <>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Tanggal Pengajuan</FormLabel>
                        <FormInput type="text" value={formatDate(selectedReport.tanggalPengajuan || selectedReport.created_at)} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Kelas</FormLabel>
                        <FormInput type="text" value={selectedReport.student?.class || selectedReport.kelas || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>NIS</FormLabel>
                        <FormInput type="text" value={selectedReport.student?.nis || selectedReport.nis || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Tanggal Lembur</FormLabel>
                        <FormInput type="text" value={formatDate(selectedReport.tanggalLembur || selectedReport.overtime_date)} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Jam Mulai</FormLabel>
                        <FormInput type="text" value={selectedReport.jamMulai || selectedReport.start_time || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Jam Selesai</FormLabel>
                        <FormInput type="text" value={selectedReport.jamSelesai || selectedReport.finish_time || selectedReport.end_time || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Nama Guru Bengkel</FormLabel>
                        <FormInput type="text" value={selectedReport.teacher_name || selectedReport.guruBengkel || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Alasan</FormLabel>
                        <FormTextarea value={selectedReport.purpose || selectedReport.alasan || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Jenis Pekerjaan</FormLabel>
                        <FormTextarea value={selectedReport.type_or_work || selectedReport.jenisPekerjaan || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Refleksi</FormLabel>
                        <FormTextarea value={selectedReport.refleksi || selectedReport.reflection || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Lampiran Gambar</FormLabel>
                        {selectedReport.images && selectedReport.images.length > 0 ? (
                          <ImagesGrid>
                            {selectedReport.images.map((image, index) => {
                              // Handle different image formats similar to AdminLaporanLemburPage
                              let imgSrc = '';
                              if (typeof image === 'string') {
                                imgSrc = image;
                              } else if (image && image.image_url) {
                                // API returns image objects with image_url property
                                const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                                imgSrc = image.image_url.startsWith('http') 
                                  ? image.image_url 
                                  : `${baseUrl}/${image.image_url}`;
                              }
                              
                              if (!imgSrc) {
                                return null;
                              }
                              
                              return (
                                <ImageContainer key={index}>
                                  <img
                                    src={imgSrc}
                                    alt={`Lampiran ${index + 1}`}
                                    onClick={() => window.open(imgSrc, '_blank')}
                                    onError={(e) => {
                                      console.error('Image failed to load:', imgSrc);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </ImageContainer>
                              );
                            })}
                          </ImagesGrid>
                        ) : (
                          <NoImageText>Tidak ada lampiran gambar</NoImageText>
                        )}
                      </FormGroup>
                    </FormRow>
                  </>
                )}

                {reportType === 'kersos' && (
                  <>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Tanggal Kersos</FormLabel>
                        <FormInput type="text" value={formatDate(selectedReport.kersos_date || selectedReport.tanggalKersos)} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Nomor Telepon</FormLabel>
                        <FormInput type="text" value={selectedReport.pic_contact_number || selectedReport.nomorTelepon || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Jam Mulai</FormLabel>
                        <FormInput type="text" value={selectedReport.start_time || selectedReport.jamMulai || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Jam Selesai</FormLabel>
                        <FormInput type="text" value={selectedReport.finish_time || selectedReport.jamSelesai || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Jenis Pekerjaan</FormLabel>
                        <FormTextarea value={selectedReport.type_or_work || selectedReport.jenisPekerjaan || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Refleksi</FormLabel>
                        <FormTextarea value={selectedReport.reflection || selectedReport.refleksi || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                       <FormGroup>
                         <FormLabel>PIC</FormLabel>
                         <FormInput type="text" value={selectedReport.pic || '-'} readOnly />
                       </FormGroup>
                     </FormRow>
                   </>
                 )}

                {/* Images Section for kersos reports */}
                {reportType === 'kersos' && selectedReport.images && selectedReport.images.length > 0 && (
                  <>
                    <FormGroup>
                      <FormLabel>Lampiran Gambar</FormLabel>
                      <ImagesGrid>
                        {selectedReport.images.map((image, index) => {
                          // Handle different image formats
                          let imgSrc = '';
                          if (typeof image === 'string') {
                            imgSrc = image;
                          } else if (image && image.image_url) {
                            // API returns image objects with image_url property
                            const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                            imgSrc = image.image_url.startsWith('http') 
                              ? image.image_url 
                              : `${baseUrl}/${image.image_url}`;
                          }
                          
                          if (!imgSrc) {
                            return null;
                          }
                          
                          return (
                            <ImageContainer key={index}>
                              <img
                                src={imgSrc}
                                alt={`Lampiran ${index + 1}`}
                                onClick={() => window.open(imgSrc, '_blank')}
                                onError={(e) => {
                                  console.error('Image failed to load:', imgSrc);
                                  e.target.style.display = 'none';
                                }}
                              />
                            </ImageContainer>
                          );
                        })}
                      </ImagesGrid>
                    </FormGroup>
                  </>
                )}

                {/* Images Section for kersos reports */}
                {reportType === 'kersos' && (!selectedReport.images || selectedReport.images.length === 0) && (
                  <FormGroup>
                    <FormLabel>Lampiran Gambar</FormLabel>
                    <NoImageText>Tidak ada lampiran gambar</NoImageText>
                  </FormGroup>
                )}

                {reportType === 'kersos' && selectedReport.images && selectedReport.images.length > 0 && (
                  <>
                    <FormGroup>
                      <FormLabel>Lampiran Gambar</FormLabel>
                      <ImagesGrid>
                        {selectedReport.images.map((image, index) => {
                          // Handle different image formats
                          let imgSrc = '';
                          if (typeof image === 'string') {
                            imgSrc = image;
                          } else if (image && image.image_url) {
                            // API returns image objects with image_url property
                            const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                            imgSrc = image.image_url.startsWith('http') 
                              ? image.image_url 
                              : `${baseUrl}/${image.image_url}`;
                          }
                          
                          if (!imgSrc) {
                            return null;
                          }
                          
                          return (
                            <ImageContainer key={index}>
                              <img
                                src={imgSrc}
                                alt={`Lampiran ${index + 1}`}
                                onClick={() => window.open(imgSrc, '_blank')}
                                onError={(e) => {
                                  console.error('Image failed to load:', imgSrc);
                                  e.target.style.display = 'none';
                                }}
                              />
                            </ImageContainer>
                          );
                        })}
                      </ImagesGrid>
                    </FormGroup>
                  </>
                )}



                {reportType === 'psm' && (
                  <>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Tanggal PSM</FormLabel>
                        <FormInput type="text" value={formatDate(selectedReport.psm_date || selectedReport.tanggalPSM)} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Kelas</FormLabel>
                        <FormInput type="text" value={selectedReport.student?.class || selectedReport.class_name || selectedReport.kelas || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Lokasi</FormLabel>
                        <FormInput type="text" value={selectedReport.location || selectedReport.lokasi || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Jam Mulai</FormLabel>
                        <FormInput type="text" value={selectedReport.start_time || selectedReport.jamMulai || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Jam Selesai</FormLabel>
                        <FormInput type="text" value={selectedReport.finish_time || selectedReport.end_time || selectedReport.jamSelesai || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Apresiasi</FormLabel>
                        <FormInput type="text" value={selectedReport.apresiasi || selectedReport.appreciation || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                       <FormGroup>
                         <FormLabel>Poin Apresiasi</FormLabel>
                         <FormInput type="text" value={selectedReport.apresiasiPoint || selectedReport.appreciation_point || '0'} readOnly />
                       </FormGroup>
                     </FormRow>
                     <FormRow>
                      <FormGroup>
                        <FormLabel>Deskripsi</FormLabel>
                        <FormTextarea value={selectedReport.description || selectedReport.deskripsi || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Refleksi</FormLabel>
                        <FormTextarea value={selectedReport.reflection || selectedReport.refleksi || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>PIC</FormLabel>
                        <FormInput type="text" value={selectedReport.person_in_charge || selectedReport.pic || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    
                    {/* Images Section for PSM reports - moved to bottom */}
                    {(!selectedReport.images || selectedReport.images.length === 0) && (
                      <FormRow>
                        <FormGroup>
                          <FormLabel>Lampiran Gambar</FormLabel>
                          <NoImageText>Tidak ada lampiran gambar</NoImageText>
                        </FormGroup>
                      </FormRow>
                    )}

                    {selectedReport.images && selectedReport.images.length > 0 && (
                      <FormRow>
                        <FormGroup>
                          <FormLabel>Lampiran Gambar</FormLabel>
                          <ImagesGrid>
                            {selectedReport.images.map((image, index) => {
                              // Handle different image formats
                              let imgSrc = '';
                              if (typeof image === 'string') {
                                imgSrc = image;
                              } else if (image && image.image_url) {
                                // API returns image objects with image_url property
                                const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                                imgSrc = image.image_url.startsWith('http') 
                                  ? image.image_url 
                                  : `${baseUrl}/${image.image_url}`;
                              }
                              
                              if (!imgSrc) {
                                return null;
                              }
                              
                              return (
                                <ImageContainer key={index}>
                                  <img
                                    src={imgSrc}
                                    alt={`Lampiran ${index + 1}`}
                                    onClick={() => window.open(imgSrc, '_blank')}
                                    onError={(e) => {
                                      console.error('Image failed to load:', imgSrc);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </ImageContainer>
                              );
                            })}
                          </ImagesGrid>
                        </FormGroup>
                      </FormRow>
                    )}
                  </>
                )}

                {reportType === 'konseling' && (
                  <>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Guru BK</FormLabel>
                        <FormInput type="text" value={selectedReport.counselor_name || selectedReport.counselor?.name || selectedReport.admin_user?.name || selectedReport.teacher_name || selectedReport.teacher?.name || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Tipe Pendampingan</FormLabel>
                        <FormInput type="text" value={selectedReport.type || selectedReport.tipe_pendampingan || selectedReport.counseling_type || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Kelas</FormLabel>
                        <FormInput type="text" value={selectedReport.student?.class || selectedReport.kelas || selectedReport.class_name || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Waktu</FormLabel>
                        <FormInput type="text" value={selectedReport.time || selectedReport.waktu || selectedReport.counseling_time || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    
                    <FormRow>
                      <FormGroup fullWidth>
                        <FormLabel>Catatan</FormLabel>
                        <FormTextarea 
                          value={selectedReport.notes || selectedReport.catatan || selectedReport.description || selectedReport.keterangan || '-'} 
                          readOnly 
                        />
                      </FormGroup>
                    </FormRow>
                  </>
                )}

                {reportType === 'sp' && (
                  <>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Kelas</FormLabel>
                        <FormInput type="text" value={selectedReport.kelas || selectedReport.class || selectedReport.student?.class || selectedReport.class_name || selectedReport.className || className || '-'} readOnly />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Nama Murid</FormLabel>
                        <FormInput type="text" value={selectedReport.namaMurid || selectedReport.student_name || selectedReport.student?.name || selectedReport.nama_siswa || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <FormLabel>Tipe Peringatan</FormLabel>
                        <div style={{ padding: '0.75rem' }}>
                          <TypeBadge type={selectedReport.tipe || selectedReport.warning_type || selectedReport.type}>
                            {selectedReport.tipe || selectedReport.warning_type || selectedReport.type || '-'}
                          </TypeBadge>
                        </div>
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Total Poin</FormLabel>
                        <FormInput type="text" value={selectedReport.points || selectedReport.total_poin || '-'} readOnly />
                      </FormGroup>
                    </FormRow>
                  </>
                )}

                {selectedReport.teacher && (
                  <FormRow>
                    <FormGroup>
                      <FormLabel>Guru/Pelapor</FormLabel>
                      <FormInput type="text" value={selectedReport.teacher.name || '-'} readOnly />
                    </FormGroup>
                  </FormRow>
                )}
              </DetailForm>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const BackButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  
  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const LoadingText = styled.div`
  font-size: 1.125rem;
  color: var(--text-light);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  gap: 1rem;
`;

const ErrorText = styled.div`
  font-size: 1.125rem;
  color: var(--danger, #c01c28);
  text-align: center;
`;

const RefreshButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  background-color: var(--background);
  border-radius: 16px;
  box-shadow: var(--box-shadow);
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
  color: var(--text-light);
  margin: 0;
  font-weight: 500;
`;

const ReportsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: var(--background);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--box-shadow);

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }

  th {
    background-color: var(--background-light);
    font-weight: 600;
    color: var(--text-dark);
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover {
    background-color: var(--background-light);
  }

  @media (max-width: 1024px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;

const KeteranganCell = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'pending' || status === 'menunggu') return '#fef3c7';
    if (status === 'approved' || status === 'disetujui') return '#d1fae5';
    if (status === 'rejected' || status === 'ditolak') return '#fee2e2';
    if (status === 'completed' || status === 'selesai') return '#e0e7ff';
    return '#f3f4f6';
  }};
  color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'pending' || status === 'menunggu') return '#d97706';
    if (status === 'approved' || status === 'disetujui') return '#059669';
    if (status === 'rejected' || status === 'ditolak') return '#dc2626';
    if (status === 'completed' || status === 'selesai') return '#4f46e5';
    return '#6b7280';
  }};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  
  ${props => {
    const type = props.type?.toLowerCase();
    if (type === 'sp1') {
      return `
        background-color: #fef3c7;
        color: #92400e;
      `;
    } else if (type === 'sp2') {
      return `
        background-color: #fed7aa;
        color: #c2410c;
      `;
    } else if (type === 'sp3') {
      return `
        background-color: #fecaca;
        color: #dc2626;
      `;
    } else {
      return `
        background-color: #f3f4f6;
        color: #374151;
      `;
    }
  }}
`;

const ActionButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--primary-dark);
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background-color: var(--background-light);
    color: var(--text-dark);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const DetailForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const FormGroup = styled.div`
  flex: ${props => props.fullWidth ? '1 1 100%' : '1'};
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--text-light);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--background-light);
  color: var(--text-dark);
  font-size: 0.875rem;
  
  &:read-only {
    cursor: default;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--background-light);
  color: var(--text-dark);
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  
  &:read-only {
    cursor: default;
  }
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  min-height: 120px;
  max-height: 120px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const NoImageText = styled.p`
  font-size: 0.875rem;
  color: var(--text-light);
  margin: 0;
  padding: 1rem;
  text-align: center;
  background-color: var(--background-light);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-style: italic;
`;

export default WaliKelasReportsDetailPage;